import { create } from 'zustand'
import { getAxiosInstance } from '@/network/axiosInstance'

// Define types for files and file groups
interface FileType {
  id: string
  filename: string
  url: string
  groupId: string | null
  size: number
  contentType: string
  createdAt: string
}

interface FileGroupType {
  id: string
  name: string
  projectId: string
  userId: string
  files: FileType[]
  createdAt: string
  updatedAt: string
}

interface FileStoreState {
  files: FileType[]
  fileGroups: FileGroupType[]
  currentProjectId: string | null
  isLoading: boolean
  error: string | null

  // Set the active project ID
  setProjectId: (projectId: string) => void

  // File operations
  fetchFiles: (projectId?: string) => Promise<void>
  uploadFile: (
    formData: FormData,
    groupId?: string | null
  ) => Promise<FileType | null>
  uploadStandaloneFile: (formData: FormData) => Promise<FileType | null>
  deleteFile: (fileId: string) => Promise<boolean>
  downloadFile: (fileId: string) => Promise<void>
  searchFiles: (projectId: string, searchTerm: string) => Promise<void>
  moveFileToGroup: (
    fileId: string,
    groupId: string | null
  ) => Promise<FileType | null>

  // File group operations
  fetchFileGroups: (projectId?: string) => Promise<void>
  createFileGroup: (
    name: string,
    projectId?: string
  ) => Promise<FileGroupType | null>
  updateFileGroup: (
    groupId: string,
    name: string
  ) => Promise<FileGroupType | null>
  deleteFileGroup: (groupId: string) => Promise<boolean>
  getFilesByGroup: (groupId: string) => Promise<void>
}

export const useFileStore = create<FileStoreState>((set, get) => ({
  files: [],
  fileGroups: [],
  currentProjectId: null,
  isLoading: false,
  error: null,

  setProjectId: (projectId: string) => {
    set({ currentProjectId: projectId })
  },

  // File operations
  fetchFiles: async (projectId?: string) => {
    const pid = projectId || get().currentProjectId
    if (!pid) {
      set({ error: 'No project ID provided' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      // Explicitly include projectId as a query parameter
      const response = await getAxiosInstance().get(
        `/api/projects/${pid}/files?projectId=${pid}`
      )
      set({ files: response.data.files || [], isLoading: false })
    } catch (error) {
      console.error('Error fetching files:', error)
      set({
        files: [],
        isLoading: false,
        error: error.response?.data?.error || 'Failed to fetch files',
      })
    }
  },

  uploadFile: async (formData: FormData, groupId?: string | null) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return null
    }

    // Add projectId and userId to form data
    formData.append('projectId', projectId)
    // Use groupId if provided
    if (groupId) {
      formData.append('groupId', groupId)
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().post(
        `/api/projects/${projectId}/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      // Add the new file to our state
      const newFile = response.data
      set((state) => ({
        files: [...state.files, newFile],
        isLoading: false,
      }))

      // If the file was added to a group, refresh the file groups
      if (groupId) {
        get().fetchFileGroups()
      }

      return newFile
    } catch (error) {
      console.error('Error uploading file:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to upload file',
      })
      return null
    }
  },

  uploadStandaloneFile: async (formData: FormData) => {
    console.log("uploadStandaloneFile: formData contents:");
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`${key}${value}:`, {
          name: value.name,
          type: value.type,
          size: value.size
        });
      } else {
        console.log(`${key}:`, value);
      }
    }

    console.log("currentProjectId:", get().currentProjectId);
    const projectId = get().currentProjectId
    console.log("projectId:", projectId)
    if (!projectId) {
      set({ error: 'No project ID provided' });
      return null;
    }

    if (!formData.has('projectId')) {
      formData.append('projectId', projectId);
    }

    set({ isLoading: true, error: null });
    try {
      console.log('Standalone file upload - formData contents:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      console.log('Standalone file upload - full request details:');
      console.log('URL:', `/api/projects/${projectId}/files/standalone`);
      console.log('Method:', 'POST');
      console.log('Headers:', {
        'Content-Type': 'multipart/form-data',
      });
      console.log('FormData:', JSON.stringify(formData));

      const response = await getAxiosInstance().post(
        `/api/projects/${projectId}/files/standalone`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('Standalone file upload - response:', response);

      const newFile = {
        ...response.data,
        blobName: response.data.blobName, // Capture blobName
      };
      set((state) => ({
        files: [...state.files, newFile],
        isLoading: false,
      }));

      return newFile;
    } catch (error) {
      console.error('Error uploading standalone file:', error);
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to upload file',
      });
      return null;
    }
  },

  deleteFile: async (fileId: string) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return false
    }

    set({ isLoading: true, error: null })
    try {
      await getAxiosInstance().delete(
        `/api/projects/${projectId}/files/${fileId}`
      )

      // Remove the file from our state
      set((state) => ({
        files: state.files.filter((file) => file.id !== fileId),
        isLoading: false,
      }))

      // Refresh file groups as the file might have been in a group
      get().fetchFileGroups()

      return true
    } catch (error) {
      console.error('Error deleting file:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to delete file',
      })
      return false
    }
  },

  downloadFile: async (fileId: string) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().get(
        `/api/projects/${projectId}/files/${fileId}`,
        {
          responseType: 'blob',
        }
      )

      // Find the file in our state to get the filename
      const file = get().files.find((f) => f.id === fileId)
      const filename = file?.filename || 'download'

      // Create a blob URL and initiate download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()

      // Clean up
      window.URL.revokeObjectURL(url)
      link.remove()
      set({ isLoading: false })
    } catch (error) {
      console.error('Error downloading file:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to download file',
      })
    }
  },

  searchFiles: async (projectId: string, searchTerm: string) => {
    const pid = projectId || get().currentProjectId
    if (!pid) {
      set({ error: 'No project ID provided' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().get(
        `/api/projects/${pid}/files/search?projectId=${pid}&searchTerm=${encodeURIComponent(searchTerm)}`
      )

      set({ files: response.data.files || [], isLoading: false })
    } catch (error) {
      console.error('Error searching files:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to search files',
      })
    }
  },

  moveFileToGroup: async (fileId: string, groupId: string | null) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return null
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().put(
        `/api/projects/${projectId}/files/${fileId}/move`,
        { groupId }
      )

      // Update the file in our state
      const updatedFile = response.data.file
      set((state) => ({
        files: state.files.map((file) =>
          file.id === fileId ? updatedFile : file
        ),
        isLoading: false,
      }))

      // Refresh file groups to reflect the change
      get().fetchFileGroups()

      return updatedFile
    } catch (error) {
      console.error('Error moving file to group:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to move file',
      })
      return null
    }
  },

  // File group operations
  fetchFileGroups: async (projectId?: string) => {
    const pid = projectId || get().currentProjectId
    if (!pid) {
      set({ error: 'No project ID provided' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().get(
        `/api/projects/${pid}/filegroups`
      )
      set({ fileGroups: response.data.fileGroups || [], isLoading: false })
    } catch (error) {
      console.error('Error fetching file groups:', error)
      set({
        fileGroups: [],
        isLoading: false,
        error: error.response?.data?.error || 'Failed to fetch file groups',
      })
    }
  },

  createFileGroup: async (name: string, projectId?: string) => {
    const pid = projectId || get().currentProjectId
    if (!pid) {
      set({ error: 'No project ID provided' })
      return null
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().post(
        `/api/projects/${pid}/filegroups`,
        { name, projectId: pid }
      )

      // Add the new group to our state
      const newGroup = response.data
      set((state) => ({
        fileGroups: [...state.fileGroups, newGroup],
        isLoading: false,
      }))

      return newGroup
    } catch (error) {
      console.error('Error creating file group:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to create file group',
      })
      return null
    }
  },

  updateFileGroup: async (groupId: string, name: string) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return null
    }

    set({ isLoading: true, error: null })
    try {
      const response = await getAxiosInstance().put(
        `/api/projects/${projectId}/filegroups/${groupId}`,
        { name }
      )

      // Update the group in our state
      const updatedGroup = response.data
      set((state) => ({
        fileGroups: state.fileGroups.map((group) =>
          group.id === groupId ? updatedGroup : group
        ),
        isLoading: false,
      }))

      return updatedGroup
    } catch (error) {
      console.error('Error updating file group:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to update file group',
      })
      return null
    }
  },

  deleteFileGroup: async (groupId: string) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return false
    }

    set({ isLoading: true, error: null })
    try {
      await getAxiosInstance().delete(
        `/api/projects/${projectId}/filegroups/${groupId}`
      )

      // Remove the group from our state
      set((state) => ({
        fileGroups: state.fileGroups.filter((group) => group.id !== groupId),
        isLoading: false,
      }))

      // Refresh files as the files in the group might have been deleted
      get().fetchFiles()

      return true
    } catch (error) {
      console.error('Error deleting file group:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to delete file group',
      })
      return false
    }
  },

  getFilesByGroup: async (groupId: string) => {
    const projectId = get().currentProjectId
    if (!projectId) {
      set({ error: 'No project ID provided' })
      return
    }

    set({ isLoading: true, error: null })
    try {
      // Include both projectId and groupId as query parameters
      const response = await getAxiosInstance().get(
        `/api/projects/${projectId}/files?projectId=${projectId}&groupId=${groupId}`
      )

      // Update our state with the files from this group
      set({ files: response.data.files || [], isLoading: false })
    } catch (error) {
      console.error('Error getting files by group:', error)
      set({
        isLoading: false,
        error: error.response?.data?.error || 'Failed to get files by group',
      })
    }
  },
}))
